import React, { useState, useEffect, useRef } from 'react';
import { makeStyles, TextField, Button, IconButton, Typography } from '@material-ui/core';
import { Annotation } from 'models';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { FormattedMessage } from 'react-intl';

interface ImageAnnotatorProps {
  src: string;
  alt?: string;
  annotations: Annotation[];
  onAnnotationsChange?: (
    annotations: Annotation[] | Annotation,
    action: 'add' | 'update' | 'delete',
  ) => void;
  tempAnnotation?: Annotation;
  onTempAnnotationChange?: (annotation: Annotation) => void;
  className?: string;
  disableTextEditor?: boolean;
  readOnly?: boolean;
  disableZoom?: boolean;
}

const useStyles = makeStyles((theme) => {
  return {
    container: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      maxHeight: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      overscrollBehavior: 'contain',
    },
    imageContainer: {
      position: 'relative',
      transformOrigin: 'top left',
      cursor: 'grab',
      width: '95%',
      '&:active': {
        cursor: 'grabbing',
      },
    },
    imageContainerWithTransition: {
      transition: 'transform 0.1s ease-out',
    },
    image: {
      width: '100%',
      display: 'block',
    },
    annotationCanvas: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      cursor: 'crosshair',
    },
    annotationSelector: {
      position: 'absolute',
      border: '2px dashed #007DC5',
      backgroundColor: 'rgba(29, 47, 102, 0.2)',
      pointerEvents: 'none',
    },
    annotation: {
      position: 'absolute',
      border: '2px solid',
      boxSizing: 'border-box',
      cursor: 'pointer',
    },
    annotationText: {
      position: 'absolute',
      backgroundColor: 'white',
      padding: theme.spacing(1),
      borderRadius: theme.spacing(0.5),
      zIndex: 90,
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
      color: '#000',
    },
    annotationEditor: {
      position: 'absolute',
      backgroundColor: 'white',
      padding: theme.spacing(1),
      borderRadius: theme.spacing(0.5),
      zIndex: 95,
      width: '250px',
      maxHeight: '150px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
      color: '#000',
      border: '1px solid #ccc',
    },
    annotationForm: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: theme.spacing(1),
    },
    zoomInfo: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      color: 'white',
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.spacing(0.5),
      fontSize: '0.8rem',
      zIndex: 91,
    },
  };
});

const LONG_PRESS_DURATION = 200;
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const ZOOM_STEP = 0.4;

const ImageAnnotator: React.FC<ImageAnnotatorProps> = (props) => {
  const {
    src,
    alt,
    annotations,
    onAnnotationsChange,
    tempAnnotation,
    onTempAnnotationChange,
    className,
    disableTextEditor = false,
    readOnly = false,
    disableZoom = false,
  } = props;

  const classes = useStyles(props);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const pressTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });

  const [activeAnnotationId, setActiveAnnotationId] = useState<string | null>(null);
  const [tempAnnotationText, setTempAnnotationText] = useState('');
  const [hoveredAnnotation, setHoveredAnnotation] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });

  const panRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(1);

  const updateTransform = (newPan = panRef.current, newZoom = zoomRef.current) => {
    if (imageContainerRef.current) {
      imageContainerRef.current.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${newZoom})`;
    }
  };

  const getImageRelativeCoordinates = (clientX: number, clientY: number) => {
    if (!imageRef.current || !containerRef.current) return { x: 0, y: 0 };

    const imageRect = imageRef.current.getBoundingClientRect();
    const adjustedX = (clientX - imageRect.left) / zoom;
    const adjustedY = (clientY - imageRect.top) / zoom;

    const x = (adjustedX / (imageRect.width / zoom)) * 100;
    const y = (adjustedY / (imageRect.height / zoom)) * 100;

    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
  };
  const handleWheel = (e: React.WheelEvent) => {
    if (readOnly || disableZoom) return null;
    e.stopPropagation();
    if (!containerRef.current || !imageRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();

    const mouseX = e.clientX - containerRect.left;
    const mouseY = e.clientY - containerRect.top;

    const zoomDelta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom + zoomDelta));

    if (newZoom === zoom) return;

    if (newZoom === 1) {
      const newPan = { x: 0, y: 0 };
      setZoom(1);
      setPan(newPan);
      zoomRef.current = 1;
      panRef.current = newPan;
      updateTransform(newPan, 1);
      return;
    }

    const pointX = (mouseX - pan.x) / zoom;
    const pointY = (mouseY - pan.y) / zoom;

    const newPanX = mouseX - pointX * newZoom;
    const newPanY = mouseY - pointY * newZoom;

    const newPan = { x: newPanX, y: newPanY };
    setZoom(newZoom);
    setPan(newPan);
    zoomRef.current = newZoom;
    panRef.current = newPan;
    updateTransform(newPan, newZoom);
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    if (readOnly) return;

    if (e.button === 1 && zoom > 1) {
      e.preventDefault();
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      return;
    }

    if (e.button !== 0) return;

    // Don't start drawing if clicking on an annotation
    if ((e.target as HTMLElement).classList.contains('annotation-clickable')) {
      return;
    }

    if (e.target === e.currentTarget) {
      e.preventDefault();
    }
    onTempAnnotationChange(null);
    setActiveAnnotationId(null);
    setTempAnnotationText('');
    const pos = getImageRelativeCoordinates(e.clientX, e.clientY);
    setStartPos(pos);
    setCurrentPos(pos);

    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
    }

    pressTimerRef.current = setTimeout(() => {
      setIsDrawing(true);
      pressTimerRef.current = null;
    }, LONG_PRESS_DURATION);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      e.preventDefault();
      const deltaX = e.clientX - lastPanPoint.x;
      const deltaY = e.clientY - lastPanPoint.y;

      panRef.current = {
        x: panRef.current.x + deltaX,
        y: panRef.current.y + deltaY,
      };

      updateTransform();
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      return;
    }

    if (readOnly || !isDrawing) return;

    const pos = getImageRelativeCoordinates(e.clientX, e.clientY);
    setCurrentPos(pos);
  };
  const handleMouseUp = (e: React.MouseEvent) => {
    if (isPanning) {
      setIsPanning(false);
      setPan(panRef.current);
      return;
    }

    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
      return;
    }

    if (readOnly || !isDrawing) {
      setIsDrawing(false);
      return;
    }

    setIsDrawing(false);

    const width = Math.abs(currentPos.x - startPos.x);
    const height = Math.abs(currentPos.y - startPos.y);

    if (width > 1 && height > 1) {
      const newTempAnnotation = new Annotation({
        x: Math.min(startPos.x, currentPos.x),
        y: Math.min(startPos.y, currentPos.y),
        width,
        height,
        text: '',
      });
      onTempAnnotationChange(newTempAnnotation);
      if (!disableTextEditor) {
        setActiveAnnotationId('temp');
        setTempAnnotationText('');
      }
    } else {
      onTempAnnotationChange(null);
    }
  };
  const saveAnnotation = () => {
    if (activeAnnotationId === 'temp' && tempAnnotation) {
      const newAnnotation: Annotation = {
        ...tempAnnotation,
        id: `annotation-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        text: tempAnnotationText,
      };
      onAnnotationsChange(newAnnotation, 'add');
      onTempAnnotationChange(null);
    } else if (activeAnnotationId) {
      const annotation = annotations.find((a) => {
        return a.id === activeAnnotationId;
      });
      if (annotation) {
        const updatedAnnotation = { ...annotation, text: tempAnnotationText };
        onAnnotationsChange(updatedAnnotation, 'update');
      }
    }
    setActiveAnnotationId(null);
    setTempAnnotationText('');
  };
  const deleteAnnotation = (id: string) => {
    if (id === 'temp') {
      onTempAnnotationChange(null);
      setActiveAnnotationId(null);
      setTempAnnotationText('');
      return;
    }
    const annotation = annotations.find((a) => {
      return a.id === id;
    });
    if (annotation) {
      onAnnotationsChange(annotation, 'delete');
      if (activeAnnotationId === id) {
        setActiveAnnotationId(null);
        setTempAnnotationText('');
      }
    }
  };
  const cancelEdit = () => {
    if (activeAnnotationId === 'temp') {
      onTempAnnotationChange(null);
      setActiveAnnotationId(null);
      setTempAnnotationText('');
    } else if (activeAnnotationId) {
      const annotation = annotations.find((a) => {
        return a.id === activeAnnotationId;
      });
      if (annotation && annotation.text === '') {
        deleteAnnotation(activeAnnotationId);
      } else {
        setActiveAnnotationId(null);
        setTempAnnotationText('');
      }
    }
  };

  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (e.detail === 2) {
      resetZoom();
    }
  };
  // Effect to handle tempAnnotation changes
  useEffect(() => {
    if (!tempAnnotation && activeAnnotationId === 'temp') {
      setActiveAnnotationId(null);
      setTempAnnotationText('');
    }
  }, [tempAnnotation, activeAnnotationId]);

  useEffect(() => {
    return () => {
      if (pressTimerRef.current) {
        clearTimeout(pressTimerRef.current);
      }
    };
  }, []);

  let selectionRect = null;
  if (isDrawing) {
    selectionRect = {
      left: `${Math.min(startPos.x, currentPos.x)}%`,
      top: `${Math.min(startPos.y, currentPos.y)}%`,
      width: `${Math.abs(currentPos.x - startPos.x)}%`,
      height: `${Math.abs(currentPos.y - startPos.y)}%`,
    };
  }
  const getEditorPosition = (id: string) => {
    let annotationToPosition = null;
    if (id === 'temp') {
      annotationToPosition = tempAnnotation;
    } else {
      annotationToPosition = annotations.find((a) => {
        return a.id === id;
      });
    }
    if (!annotationToPosition || !imageRef.current || !containerRef.current) return {};

    // Get the actual pixel position of the annotation on screen
    const imageRect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    // Convert percentage to actual pixels on the transformed image
    const annotationPixelX = (annotationToPosition.x / 100) * (imageRect.width / zoom);
    const annotationPixelY = (annotationToPosition.y / 100) * (imageRect.height / zoom);
    const annotationPixelWidth = (annotationToPosition.width / 100) * (imageRect.width / zoom);
    const annotationPixelHeight = (annotationToPosition.height / 100) * (imageRect.height / zoom);

    // Calculate screen position considering zoom and pan
    const screenX = annotationPixelX * zoom + pan.x;
    const screenY = annotationPixelY * zoom + pan.y;
    const screenWidth = annotationPixelWidth * zoom;
    const screenHeight = annotationPixelHeight * zoom;

    // Editor dimensions
    const editorWidth = 250;
    const editorHeight = 150;

    // Calculate container dimensions
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    // Preferred position: to the right and below the annotation
    let editorLeft = screenX + screenWidth + 10;
    let editorTop = screenY + screenHeight + 10;

    // Check if editor would overflow horizontally
    if (editorLeft + editorWidth > containerWidth) {
      // Try positioning to the left of the annotation
      if (screenX >= editorWidth + 10) {
        editorLeft = screenX - editorWidth - 10;
      } else {
        // Keep it within bounds on the right side
        editorLeft = containerWidth - editorWidth - 10;
      }
    }

    // Check if editor would overflow vertically
    if (editorTop + editorHeight > containerHeight) {
      // Try positioning above the annotation
      if (screenY >= editorHeight + 10) {
        editorTop = screenY - editorHeight - 10;
      } else {
        // Keep it within bounds at the bottom
        editorTop = containerHeight - editorHeight - 10;
      }
    }

    // Ensure minimum bounds
    editorLeft = Math.max(10, Math.min(editorLeft, containerWidth - editorWidth - 10));
    editorTop = Math.max(10, Math.min(editorTop, containerHeight - editorHeight - 10));

    return {
      left: `${editorLeft}px`,
      top: `${editorTop}px`,
    };
  };

  const getTextBoxStyling = (annotation: Annotation) => {
    const top = annotation.y + annotation.height + 0.5;
    return {
      left: `${annotation.x}%`,
      top: `${top}%`,
    } as CSSProperties;
  };

  const imageContainerStyle = {
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
  };

  return (
    <div ref={containerRef} className={`${classes.container} ${className || ''}`}>
      <div
        ref={imageContainerRef}
        className={classes.imageContainer}
        style={imageContainerStyle}
        onWheel={handleWheel}
      >
        <img ref={imageRef} src={src} alt={alt || 'Annotatable image'} className={classes.image} />

        <div
          className={classes.annotationCanvas}
          style={readOnly ? { cursor: 'auto' } : {}}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onDoubleClick={handleDoubleClick}
          onMouseLeave={() => {
            if (pressTimerRef.current) {
              clearTimeout(pressTimerRef.current);
              pressTimerRef.current = null;
            }
            if (isDrawing) {
              setIsDrawing(false);
            }
            if (isPanning) {
              setIsPanning(false);
            }
          }}
        >
          {' '}
          {annotations.map((annotation) => {
            return (
              <React.Fragment key={annotation.id}>
                <div
                  className={`${classes.annotation} annotation-clickable`}
                  style={{
                    left: `${annotation.x}%`,
                    top: `${annotation.y}%`,
                    width: `${annotation.width}%`,
                    height: `${annotation.height}%`,
                    borderColor: '#007DC5',
                    backgroundColor: 'rgba(29, 47, 102, 0.2)',
                    borderStyle: activeAnnotationId === annotation.id ? 'solid' : 'solid',
                    borderWidth: activeAnnotationId === annotation.id ? '3px' : '2px',
                  }}
                  onMouseEnter={() => {
                    return setHoveredAnnotation(annotation.id);
                  }}
                  onMouseLeave={() => {
                    return setHoveredAnnotation(null);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!readOnly && !disableTextEditor) {
                      setActiveAnnotationId(annotation.id);
                      setTempAnnotationText(annotation.text || '');
                    }
                  }}
                />
                {hoveredAnnotation === annotation.id && annotation.text && (
                  <div className={classes.annotationText} style={getTextBoxStyling(annotation)}>
                    <Typography>{annotation.text}</Typography>
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {tempAnnotation && (!isDrawing || activeAnnotationId === 'temp') && (
            <div
              className={`${classes.annotation} annotation-clickable`}
              style={{
                left: `${tempAnnotation.x}%`,
                top: `${tempAnnotation.y}%`,
                width: `${tempAnnotation.width}%`,
                height: `${tempAnnotation.height}%`,
                borderColor: '#007DC5',
                backgroundColor: 'rgba(29, 47, 102, 0.2)',
                borderStyle: activeAnnotationId === 'temp' ? 'solid' : 'dashed',
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (
                  !readOnly &&
                  !disableTextEditor &&
                  tempAnnotation &&
                  activeAnnotationId !== 'temp'
                ) {
                  setActiveAnnotationId('temp');
                  setTempAnnotationText('');
                }
              }}
            />
          )}
          {selectionRect && <div className={classes.annotationSelector} style={selectionRect} />}
        </div>
      </div>
      {!disableTextEditor && activeAnnotationId && (
        <div className={classes.annotationEditor} style={getEditorPosition(activeAnnotationId)}>
          <div className={classes.annotationForm}>
            <TextField
              autoFocus
              label='Annotation Note'
              variant='outlined'
              multiline
              minRows={3}
              fullWidth
              value={tempAnnotationText}
              onChange={(e) => {
                return setTempAnnotationText(e.target.value);
              }}
              size='small'
            />
            <div className={classes.buttonContainer}>
              {' '}
              <Button
                size='small'
                variant='contained'
                color='primary'
                onClick={saveAnnotation}
                disabled={activeAnnotationId === 'temp' && !tempAnnotationText.trim()}
              >
                <Typography>
                  <FormattedMessage id='remoteInjector.save' />
                </Typography>
              </Button>
              <div>
                <IconButton size='small' onClick={cancelEdit}>
                  <CloseIcon fontSize='small' />
                </IconButton>
                <IconButton
                  size='small'
                  onClick={() => {
                    return deleteAnnotation(activeAnnotationId);
                  }}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageAnnotator;
